import React, { useCallback, useMemo, useState } from "react";
import { callAPI } from "../../utils/apiUtils";
import { apiUrls } from "../../utils/apiUrls";
import { USING_PROMO_SCHEDULE_MOCK, mockPromoApi } from "../../utils/promoScheduleService";
import {
  SCHEDULE_TYPES,
  WEEKDAYS,
  TARGET_TYPES,
  filterTargetOptions,
  from12HourParts,
  describeTargetCounts,
  normalizeDays,
  normalizeTargetOptions,
  summarizePromotion,
  to12HourParts,
  validateScheduleForm,
} from "../../utils/offerSchedule";
import "../../styles/promoSchedule.css";

// Rendering thousands of checkboxes at once makes a till tablet crawl; past this many rows per
// group the list asks the user to narrow the search instead.
const MAX_ROWS_PER_GROUP = 150;

const EMPTY_OPTIONS = { categories: [], subcategories: [], products: [] };

/** Lazy loader for GET /v3/promotion/target-options. Nothing is requested until load() is called. */
export const useTargetOptions = () => {
  const [state, setState] = useState({ status: "idle", options: EMPTY_OPTIONS });

  const load = useCallback(async () => {
    setState((prev) => (prev.status === "loading" || prev.status === "ready" ? prev : { ...prev, status: "loading" }));
    try {
      const res = USING_PROMO_SCHEDULE_MOCK
        ? await mockPromoApi("target-options")
        : await callAPI(apiUrls.PROMO_TARGET_OPTIONS, {}, "GET");
      const body = res?.data || {};
      // The payload is normally under `data`; tolerate it sitting at the top level.
      const raw = body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : body;
      setState({ status: "ready", options: normalizeTargetOptions(raw) });
    } catch (err) {
      console.error("Failed to load promotion target options:", err);
      setState({ status: "error", options: EMPTY_OPTIONS });
    }
  }, []);

  const reset = useCallback(() => setState({ status: "idle", options: EMPTY_OPTIONS }), []);

  return { ...state, load, reset };
};

export const emptyScheduleForm = () => ({
  schedule_type: "always",
  schedule_days: [],
  schedule_time_from: "16:00",
  schedule_time_to: "18:00",
  target_mode: "all",
  target_tokens: [],
});

export const toScheduleArgs = (form) => ({
  schedule: {
    type: form.schedule_type,
    days: form.schedule_days,
    timeFrom: form.schedule_time_from,
    timeTo: form.schedule_time_to,
  },
  targets: { mode: form.target_mode, tokens: form.target_tokens },
});

/** Validation message (or null) for the schedule / applies-to part of the form. */
export const validateScheduleControls = (form) => {
  const { schedule, targets } = toScheduleArgs(form);
  return validateScheduleForm(schedule, targets);
};

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const TimeOfDayPicker = ({ label, value, onChange }) => {
  const parts = to12HourParts(value) || { hour12: 12, minute: 0, meridiem: "AM" };
  // A value that is not on the 5-minute grid (should not happen here) is still shown, not lost.
  const minutes = MINUTES.includes(parts.minute) ? MINUTES : [...MINUTES, parts.minute].sort((a, b) => a - b);
  const set = (patch) => onChange(from12HourParts({ ...parts, ...patch }));
  return (
    <div>
      <label className="form-label mb-1">{label}</label>
      <div className="d-flex gap-1">
        <select
          className="form-select"
          aria-label={`${label} hour`}
          value={parts.hour12}
          onChange={(e) => set({ hour12: Number(e.target.value) })}
        >
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <select
          className="form-select"
          aria-label={`${label} minute`}
          value={parts.minute}
          onChange={(e) => set({ minute: Number(e.target.value) })}
        >
          {minutes.map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, "0")}
            </option>
          ))}
        </select>
        <select
          className="form-select"
          aria-label={`${label} AM or PM`}
          value={parts.meridiem}
          onChange={(e) => set({ meridiem: e.target.value })}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

const Segmented = ({ options, value, onChange, ariaLabel }) => (
  <div className="promoSegmented" role="radiogroup" aria-label={ariaLabel}>
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        role="radio"
        aria-checked={value === opt.value}
        className={`promoSegmentedBtn${value === opt.value ? " isActive" : ""}`}
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const TargetPicker = ({ tokens, onChange, targetOptions }) => {
  const [search, setSearch] = useState("");
  const { status, options, load } = targetOptions;

  const labels = useMemo(() => {
    const map = {};
    [...options.categories, ...options.subcategories, ...options.products].forEach((o) => {
      map[o.token] = o;
    });
    return map;
  }, [options]);

  const visible = useMemo(() => filterTargetOptions(options, search), [options, search]);

  const toggle = (token) =>
    onChange((current) => (current.includes(token) ? current.filter((t) => t !== token) : [...current, token]));

  const groups = [
    { key: "categories", title: TARGET_TYPES.category.plural, rows: visible.categories },
    { key: "subcategories", title: TARGET_TYPES.subcategory.plural, rows: visible.subcategories },
    { key: "products", title: TARGET_TYPES.product.plural, rows: visible.products },
  ];
  const nothingToPick = status === "ready" && groups.every((g) => g.rows.length === 0);

  return (
    <div className="promoTargetPicker">
      {tokens.length > 0 && (
        <div className="promoChips" aria-label="Selected targets">
          {tokens.map((token) => {
            const known = labels[token];
            const kind = token[0] === "c" ? "category" : token[0] === "s" ? "subcategory" : "product";
            const name = known ? known.name : `#${token.slice(2)}`;
            return (
              <span key={token} className="promoChip">
                <span className="promoChipKind">{TARGET_TYPES[kind].label}</span> {name}
                <button
                  type="button"
                  className="promoChipRemove"
                  aria-label={`Remove ${name}`}
                  onClick={() => toggle(token)}
                >
                  &times;
                </button>
              </span>
            );
          })}
        </div>
      )}
      <small className="text-muted d-block mb-1">{describeTargetCounts(tokens)}</small>

      <input
        type="text"
        className="form-control form-control-sm mb-2"
        placeholder="Search categories, sub-categories and items..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="promoTargetList">
        {status === "loading" || status === "idle" ? (
          <small className="text-muted">Loading...</small>
        ) : status === "error" ? (
          <small className="text-muted">
            Could not load the menu.{" "}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={load}>
              Try again
            </button>
          </small>
        ) : nothingToPick ? (
          <small className="text-muted">{search.trim() ? "Nothing matches your search." : "No categories or items found."}</small>
        ) : (
          groups.map(
            (g) =>
              g.rows.length > 0 && (
                <div key={g.key} className="mb-2">
                  <div className="promoGroupTitle">{g.title}</div>
                  {g.rows.slice(0, MAX_ROWS_PER_GROUP).map((o) => (
                    <label key={o.token} className="mb-1 d-block" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        className="me-2"
                        checked={tokens.includes(o.token)}
                        onChange={() => toggle(o.token)}
                      />
                      {o.name}
                      {o.parentLabel && <span className="promoParent"> ({o.parentLabel})</span>}
                    </label>
                  ))}
                  {g.rows.length > MAX_ROWS_PER_GROUP && (
                    <small className="text-muted d-block">
                      Showing {MAX_ROWS_PER_GROUP} of {g.rows.length}. Type in the search box to narrow it down.
                    </small>
                  )}
                </div>
              )
          )
        )}
      </div>
    </div>
  );
};

/**
 * Schedule + Applies-to controls for the Add Promotion modal. Only rendered when the server says
 * the feature is on for this restaurant and the promotion is a normal one (not a voucher).
 * `form` / `onChange(patch | prev => patch)` hold the schedule_* and target_* fields from
 * emptyScheduleForm().
 */
const PromotionScheduleControls = ({ form, onChange, targetOptions }) => {
  const days = normalizeDays(form.schedule_days);
  const { schedule, targets } = toScheduleArgs(form);
  const labels = useMemo(() => {
    const map = {};
    [...targetOptions.options.categories, ...targetOptions.options.subcategories, ...targetOptions.options.products].forEach((o) => {
      map[o.token] = o;
    });
    return map;
  }, [targetOptions.options]);
  const summary = summarizePromotion(schedule, targets, labels);
  const timeOrderBad =
    form.schedule_type === "time" && form.schedule_time_from && form.schedule_time_to && form.schedule_time_from >= form.schedule_time_to;

  // Functional patches, so two quick taps in the same render never overwrite each other.
  const toggleDay = (value) =>
    onChange((prev) => {
      const current = normalizeDays(prev.schedule_days);
      return { schedule_days: current.includes(value) ? current.filter((d) => d !== value) : normalizeDays([...current, value]) };
    });

  const setTargetMode = (mode) => {
    onChange({ target_mode: mode });
    if (mode === "selected" && targetOptions.status === "idle") targetOptions.load();
  };

  return (
    <>
      <div className="col-12" data-testid="promo-schedule-controls">
        <label className="form-label d-block">Schedule</label>
        <Segmented
          ariaLabel="Schedule"
          options={SCHEDULE_TYPES}
          value={form.schedule_type}
          onChange={(v) => onChange({ schedule_type: v })}
        />

        {form.schedule_type === "days" && (
          <div className="mt-2">
            <div className="d-flex flex-wrap gap-2" role="group" aria-label="Days of the week">
              {WEEKDAYS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  aria-pressed={days.includes(d.value)}
                  title={d.long}
                  className={`promoDayChip${days.includes(d.value) ? " isActive" : ""}`}
                  onClick={() => toggleDay(d.value)}
                >
                  {d.short}
                </button>
              ))}
            </div>
            {days.length === 0 && <small className="text-danger d-block mt-1">Pick at least one day.</small>}
          </div>
        )}

        {form.schedule_type === "time" && (
          <div className="mt-2">
            <div className="row g-2">
              <div className="col-sm-6 col-md-4">
                <TimeOfDayPicker label="From" value={form.schedule_time_from} onChange={(v) => onChange({ schedule_time_from: v })} />
              </div>
              <div className="col-sm-6 col-md-4">
                <TimeOfDayPicker label="To" value={form.schedule_time_to} onChange={(v) => onChange({ schedule_time_to: v })} />
              </div>
            </div>
            <small className="text-muted d-block mt-1">Ends at the end time (not included), same day</small>
            {timeOrderBad && <small className="text-danger d-block">The start time must be before the end time.</small>}
          </div>
        )}

        <small className="text-muted d-block mt-2">
          The Valid From / Valid To dates above still bound this promotion: it only applies between those dates.
        </small>
      </div>

      <div className="col-12">
        <label className="form-label d-block">Applies to (menu items)</label>
        <Segmented
          ariaLabel="Applies to"
          options={[
            { value: "all", label: "Entire menu" },
            { value: "selected", label: "Selected" },
          ]}
          value={form.target_mode}
          onChange={setTargetMode}
        />
        {form.target_mode === "selected" && (
          <div className="mt-2">
            <TargetPicker
              tokens={form.target_tokens}
              onChange={(update) => onChange((prev) => ({ target_tokens: update(prev.target_tokens) }))}
              targetOptions={targetOptions}
            />
            <small className="text-muted d-block mt-1">
              Deals, half-and-half and custom items are not included in targeted promotions.
            </small>
          </div>
        )}
        {summary && (
          <div className="promoSummary mt-2" aria-live="polite">
            {summary}
          </div>
        )}
      </div>
    </>
  );
};

export default PromotionScheduleControls;
